import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

interface CompanyData {
  companyName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

const Privacy: React.FC = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const data = await apiRequest<CompanyData>(API_ENDPOINTS.COMPANY.PUBLIC);
        setCompanyData(data);
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCompanyInfo();
  }, []);
  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-neutral-500 mt-4">Last updated: December 2024</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-neutral-700 leading-relaxed">
                {companyData?.companyName || 'Demo Crackers'} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how your personal information is collected, used, and disclosed by {companyData?.companyName || 'Demo Crackers'} when you 
                visit our website or make purchases from us.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">📊</span>
                Information We Collect
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Personal Information</h3>
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Name and contact information (phone number, email address)</li>
                      <li>• Billing and shipping addresses</li>
                      <li>• Payment information (processed securely by our payment providers)</li>
                      <li>• Order history and preferences</li>
                      <li>• Account credentials and security information</li>
                    </ul>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Automatically Collected Information</h3>
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Device information (IP address, browser type, operating system)</li>
                      <li>• Usage data (pages visited, time spent, clicks)</li>
                      <li>• Location information (with your consent)</li>
                      <li>• Cookies and similar tracking technologies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">⚙️</span>
                How We Use Your Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-2">Order Processing</h3>
                    <ul className="text-blue-700 text-sm space-y-1">
                      <li>• Process and fulfill your orders</li>
                      <li>• Send order confirmations and updates</li>
                      <li>• Handle returns and exchanges</li>
                      <li>• Provide customer support</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 mb-2">Account Management</h3>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• Create and maintain your account</li>
                      <li>• Personalize your experience</li>
                      <li>• Save your preferences</li>
                      <li>• Provide order history</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-800 mb-2">Communication</h3>
                    <ul className="text-purple-700 text-sm space-y-1">
                      <li>• Send promotional emails (with consent)</li>
                      <li>• Important service announcements</li>
                      <li>• Safety and regulatory updates</li>
                      <li>• Customer surveys and feedback</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h3 className="font-semibold text-orange-800 mb-2">Legal & Safety</h3>
                    <ul className="text-orange-700 text-sm space-y-1">
                      <li>• Comply with legal requirements</li>
                      <li>• Verify age for fireworks purchases</li>
                      <li>• Prevent fraud and abuse</li>
                      <li>• Enforce our terms of service</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🤝</span>
                Information Sharing
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <p className="text-yellow-800 font-semibold mb-3">We do not sell your personal information to third parties.</p>
                <p className="text-yellow-700 mb-4">We may share your information in the following circumstances:</p>
                <ul className="space-y-2 text-yellow-700">
                  <li>• <strong>Service Providers:</strong> Payment processors, shipping companies, and other service providers</li>
                  <li>• <strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li>• <strong>Business Transfers:</strong> In case of merger, acquisition, or sale of business</li>
                  <li>• <strong>Consent:</strong> When you explicitly consent to sharing</li>
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔒</span>
                Data Security
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Technical Safeguards</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li>• SSL encryption for all data transmission</li>
                    <li>• Secure payment processing</li>
                    <li>• Regular security audits and updates</li>
                    <li>• Access controls and authentication</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Organizational Measures</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li>• Employee training on data protection</li>
                    <li>• Limited access to personal data</li>
                    <li>• Regular privacy assessments</li>
                    <li>• Incident response procedures</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">⚖️</span>
                Your Rights and Choices
              </h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <h3 className="font-semibold text-neutral-900 mb-2">Access & Control</h3>
                    <ul className="text-neutral-700 text-sm space-y-1">
                      <li>• Access your personal information</li>
                      <li>• Update or correct your data</li>
                      <li>• Delete your account</li>
                      <li>• Download your data</li>
                    </ul>
                  </div>
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <h3 className="font-semibold text-neutral-900 mb-2">Communication Preferences</h3>
                    <ul className="text-neutral-700 text-sm space-y-1">
                      <li>• Unsubscribe from marketing emails</li>
                      <li>• Opt out of SMS notifications</li>
                      <li>• Manage cookie preferences</li>
                      <li>• Control location sharing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🍪</span>
                Cookies and Tracking
              </h2>
              <div className="space-y-4">
                <p className="text-neutral-700">
                  We use cookies and similar technologies to improve your browsing experience, analyze site traffic, 
                  and serve personalized content.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <h3 className="font-semibold text-green-800 mb-2">Essential</h3>
                    <p className="text-green-700 text-sm">Required for site functionality</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <h3 className="font-semibold text-blue-800 mb-2">Analytics</h3>
                    <p className="text-blue-700 text-sm">Help us improve our service</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <h3 className="font-semibold text-purple-800 mb-2">Marketing</h3>
                    <p className="text-purple-700 text-sm">Personalized content and ads</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">👶</span>
                Children's Privacy
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800 font-semibold mb-2">
                  Our services are not intended for children under 18 years of age.
                </p>
                <p className="text-red-700">
                  Due to the nature of our products (fireworks), we require customers to be at least 18 years old. 
                  We do not knowingly collect personal information from children under 18. If you are a parent or 
                  guardian and believe your child has provided us with personal information, please contact us.
                </p>
              </div>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔄</span>
                Policy Updates
              </h2>
              <div className="bg-neutral-100 rounded-xl p-6">
                <p className="text-neutral-700 mb-3">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by:
                </p>
                <ul className="space-y-2 text-neutral-700">
                  <li>• Posting the new Privacy Policy on this page</li>
                  <li>• Sending you an email notification (for significant changes)</li>
                  <li>• Displaying a prominent notice on our website</li>
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-accent-50 rounded-xl p-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-4">Contact Us</h2>
              <p className="text-neutral-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              {loading ? (
                <div className="flex items-center justify-center h-20">
                  <div className="text-neutral-600">Loading contact information...</div>
                </div>
              ) : companyData ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Customer Privacy</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="mr-2">✉️</span>
                        <span className="text-neutral-700">{companyData.email}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">📞</span>
                        <span className="text-neutral-700">{companyData.phone}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Mailing Address</h3>
                    <div className="text-neutral-700 text-sm">
                      <p>{companyData.companyName} Privacy Department</p>
                      <p>{companyData.address.street}</p>
                      <p>{companyData.address.city}, {companyData.address.state} {companyData.address.zipCode}</p>
                      <p>{companyData.address.country}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-neutral-600">Contact information unavailable. Please try again later.</div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;